<template>
    <div class="sp-negocios sp-auth">
        <!-- ================= Panel izquierdo: marca ================= -->
        <aside class="sp-auth__marca">
            <picture class="sp-auth__fondo">
                <source srcset="layout/images/negocios/hero-bg.webp" type="image/webp" />
                <img src="layout/images/negocios/hero-bg.png" alt="" fetchpriority="high" decoding="async" />
            </picture>
            <span class="sp-auth__velo" aria-hidden="true"></span>

            <!-- Orbes de luz: los mismos que usa la landing, para que el panel no
                 quede como una foto plana con texto encima. -->
            <div class="sp-glow-orb sp-glow-orb--orange" style="top: 6%; right: -14%; width: 430px; height: 430px;"></div>
            <div class="sp-glow-orb sp-glow-orb--cyan" style="bottom: 4%; left: -12%; width: 330px; height: 330px;"></div>

            <div class="sp-auth__marca-contenido">
                <router-link to="/negocios" class="sp-auth__logo" aria-label="Ir a Seway Plus Negocios">
                    <MarcaNegocios tamano="lg" />
                </router-link>

                <div class="sp-eyebrow sp-eyebrow--dark sp-auth__eyebrow">
                    <span class="sp-live-dot"></span>
                    <span>Portal de anunciantes</span>
                </div>

                <h2 class="sp-auth__titular">
                    Conecta tu negocio con miles de conductores
                    <span class="sp-auth__acento">en la ruta</span>
                </h2>

                <p class="sp-auth__bajada">
                    Publica tus ofertas y haz que tu negocio aparezca justo cuando el conductor
                    te necesita.
                </p>

                <ul class="sp-auth__beneficios">
                    <li v-for="b in beneficios" :key="b.titulo">
                        <span class="sp-auth__beneficio-icono">
                            <i :class="'pi ' + b.icono" aria-hidden="true"></i>
                        </span>
                        <div>
                            <strong>{{ b.titulo }}</strong>
                            <span>{{ b.texto }}</span>
                        </div>
                    </li>
                </ul>
            </div>
        </aside>

        <!-- ================= Panel derecho: formulario ================= -->
        <main class="sp-auth__panel">
            <div class="sp-auth__utilidades">
                <span class="sp-auth__idioma">
                    <i class="pi pi-globe" aria-hidden="true"></i>
                    <span>Español</span>
                </span>
                <button type="button" class="sp-auth__ayuda" @click="irAContacto">
                    <i class="pi pi-question-circle" aria-hidden="true"></i>
                    <span>¿Necesitas ayuda?</span>
                </button>
            </div>

            <!-- Logo solo en móvil, donde el panel de marca no se muestra -->
            <router-link to="/negocios" class="sp-auth__logo sp-auth__logo--movil" aria-label="Ir a Seway Plus Negocios">
                <MarcaNegocios tamano="md" />
            </router-link>

            <div class="sp-auth__tarjeta">
                <!-- ---------- Recuperar contraseña ---------- -->
                <template v-if="modo === 'recuperar'">
                    <span class="sp-auth__avatar"><i class="pi pi-lock" aria-hidden="true"></i></span>
                    <h1 class="sp-auth__titulo">Recupera tu contraseña</h1>
                    <p class="sp-auth__sub">
                        Te enviamos un código de 6 dígitos al correo de tu cuenta.
                    </p>

                    <form novalidate @submit.prevent="enviarCodigo">
                        <div class="sp-campo">
                            <label for="sp-rec-email"><span>Correo electrónico</span></label>
                            <span class="sp-campo-icono">
                                <i class="pi pi-envelope" aria-hidden="true"></i>
                                <InputText
                                    id="sp-rec-email"
                                    v-model.trim="recuperar.email"
                                    type="email"
                                    autocomplete="email"
                                    placeholder="ejemplo@negocio.com"
                                />
                            </span>
                        </div>
                        <button type="submit" class="sp-btn sp-btn--primary sp-btn--block" :disabled="cargando">
                            <i v-if="cargando" class="pi pi-spin pi-spinner" aria-hidden="true"></i>
                            <span>{{ codigoEnviado ? 'Reenviar código' : 'Enviarme un código' }}</span>
                        </button>
                    </form>

                    <form v-if="codigoEnviado" class="sp-auth__reset" novalidate @submit.prevent="restablecer">
                        <div class="sp-campo">
                            <label for="sp-rec-codigo"><span>Código de verificación</span></label>
                            <InputMask id="sp-rec-codigo" v-model="recuperar.codigo" mask="9-9-9-9-9-9" placeholder="0-0-0-0-0-0" />
                        </div>
                        <div class="sp-campo">
                            <label for="sp-rec-pass"><span>Nueva contraseña</span></label>
                            <Password id="sp-rec-pass" v-model="recuperar.password" :feedback="false" toggleMask />
                            <p class="sp-campo__ayuda">Mínimo 8 caracteres.</p>
                        </div>
                        <div class="sp-campo">
                            <label for="sp-rec-pass2"><span>Confirmar contraseña</span></label>
                            <Password id="sp-rec-pass2" v-model="recuperar.password_confirmation" :feedback="false" toggleMask />
                        </div>
                        <button type="submit" class="sp-btn sp-btn--primary sp-btn--block" :disabled="cargando">
                            <span>Cambiar contraseña</span>
                        </button>
                    </form>

                    <button type="button" class="sp-enlace sp-auth__volver" @click="volverALogin">
                        <i class="pi pi-arrow-left" aria-hidden="true"></i>
                        <span>Volver a iniciar sesión</span>
                    </button>
                </template>

                <!-- ---------- Inicio de sesión ---------- -->
                <template v-else>
                    <span class="sp-auth__avatar"><i class="pi pi-user" aria-hidden="true"></i></span>
                    <h1 class="sp-auth__titulo">Inicia sesión</h1>
                    <p class="sp-auth__sub">Accede a tu cuenta de Seway Plus Negocios</p>

                    <form novalidate @submit.prevent="entrar">
                        <div class="sp-campo">
                            <label for="sp-login-email">
                                <span>Correo electrónico</span>
                                <span class="sp-campo__req" aria-hidden="true">*</span>
                            </label>
                            <span class="sp-campo-icono">
                                <i class="pi pi-envelope" aria-hidden="true"></i>
                                <InputText
                                    id="sp-login-email"
                                    v-model.trim="formulario.email"
                                    type="email"
                                    autocomplete="email"
                                    placeholder="ejemplo@negocio.com"
                                    :class="{ 'p-invalid': enviado && !formulario.email }"
                                    :aria-invalid="enviado && !formulario.email ? 'true' : 'false'"
                                />
                            </span>
                        </div>

                        <div class="sp-campo">
                            <label for="sp-login-pass">
                                <span>Contraseña</span>
                                <span class="sp-campo__req" aria-hidden="true">*</span>
                            </label>
                            <span class="sp-campo-icono sp-campo-icono--pass">
                                <i class="pi pi-lock" aria-hidden="true"></i>
                                <Password
                                    id="sp-login-pass"
                                    v-model="formulario.password"
                                    :feedback="false"
                                    toggleMask
                                    placeholder="Ingresa tu contraseña"
                                    :class="{ 'p-invalid': enviado && !formulario.password }"
                                />
                            </span>
                        </div>

                        <button type="button" class="sp-enlace sp-auth__olvide" @click="modo = 'recuperar'">
                            ¿Olvidaste tu contraseña?
                        </button>

                        <button type="submit" class="sp-btn sp-btn--primary sp-btn--block sp-auth__enviar" :disabled="cargando">
                            <i v-if="cargando" class="pi pi-spin pi-spinner" aria-hidden="true"></i>
                            <span>Iniciar sesión</span>
                        </button>
                    </form>

                    <!-- El bloque de Google solo aparece con credencial configurada.
                         Sin ella no se descarga ningún script externo y el acceso
                         por correo funciona igual. -->
                    <template v-if="mostrarGoogle">
                        <div class="sp-auth__separador"><span>o</span></div>
                        <div ref="botonGoogle" class="sp-auth__google"></div>
                        <p v-if="errorGoogle" class="sp-campo__error">
                            <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
                            <span>{{ errorGoogle }}</span>
                        </p>
                    </template>

                    <p class="sp-auth__registro">
                        ¿Aún no tienes cuenta?
                        <router-link to="/negocios/registro">Crear cuenta de anunciante</router-link>
                    </p>
                </template>
            </div>
        </main>

        <!-- ================= Pie ================= -->
        <footer class="sp-auth__pie">
            <MarcaNegocios tamano="sm" con-texto perezoso />

            <p class="sp-auth__pie-legal">
                &copy; {{ anio }} Seway Plus Negocios
                <span aria-hidden="true">&bull;</span>
                Carga Control
            </p>

            <nav class="sp-auth__pie-enlaces" aria-label="Enlaces legales">
                <router-link to="/terminos-condiciones">Términos y condiciones</router-link>
                <router-link to="/politica-privacidad">Política de privacidad</router-link>
            </nav>
        </footer>

        <Toast position="top-right" />
    </div>
</template>

<script>
import MarcaNegocios from '../components/MarcaNegocios';
import api, { mensajeDeError } from './api';
import { iniciarSesion, estaAutenticado } from './sesion';
import { montarBotonGoogle, googleDisponible } from './google';
import { aplicarSeo, limpiarSeo } from '../seo';
import { registrarEvento, EVENTOS } from './analitica';
// Tokens y primitivas del módulo. Esta pantalla no usa NegociosShell porque
// tiene su propia carcasa (panel de marca + pie propio), así que importa los
// estilos directamente. estilos.js está compartido, no se duplica el CSS.
import '../estilos';

export default {
    name: 'LoginAnunciante',
    components: { MarcaNegocios },
    data() {
        return {
            modo: 'login',
            cargando: false,
            enviado: false,
            codigoEnviado: false,
            errorGoogle: '',
            anio: new Date().getFullYear(),
            formulario: { email: '', password: '' },
            recuperar: { email: '', codigo: '', password: '', password_confirmation: '' },
            beneficios: [
                {
                    icono: 'pi-map-marker',
                    titulo: 'Publicidad geolocalizada',
                    texto: 'Tu oferta aparece cuando el conductor se acerca a tu establecimiento.'
                },
                {
                    icono: 'pi-users',
                    titulo: 'Público especializado',
                    texto: 'Llegas a conductores de carga que realmente necesitan tus servicios.'
                },
                {
                    icono: 'pi-calendar',
                    titulo: 'Campañas por días',
                    texto: 'Elige la duración y el nivel de visibilidad que mejor se adapte a tu negocio.'
                },
                {
                    icono: 'pi-chart-bar',
                    titulo: 'Mide tus resultados',
                    texto: 'Consulta el rendimiento de tus campañas y el interés generado.'
                }
            ]
        };
    },
    computed: {
        mostrarGoogle() {
            return googleDisponible();
        }
    },
    mounted() {
        aplicarSeo({
            titulo: 'Iniciar sesión | Seway Plus Negocios',
            descripcion: 'Accede al portal de anunciantes de Seway Plus Negocios.'
        });

        if (estaAutenticado()) {
            this.$router.replace({ path: '/negocios/portal' });
            return;
        }

        if (this.mostrarGoogle) {
            this.$nextTick(() => this.montarGoogle());
        }
    },
    unmounted() {
        limpiarSeo();
    },
    methods: {
        montarGoogle() {
            montarBotonGoogle(
                this.$refs.botonGoogle,
                (idToken) => this.entrarConGoogle(idToken),
                () => {
                    this.errorGoogle = 'No pudimos cargar el acceso con Google. Usa tu correo y contraseña.';
                }
            );
        },

        irAContacto() {
            this.$router.push({ path: '/negocios', query: { ir: 'contacto' } });
        },

        volverALogin() {
            this.modo = 'login';
            this.codigoEnviado = false;
            if (this.mostrarGoogle) {
                this.$nextTick(() => this.montarGoogle());
            }
        },

        entrar() {
            this.enviado = true;

            if (!this.formulario.email || !this.formulario.password) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Datos incompletos',
                    detail: 'Ingresa tu correo y contraseña.',
                    life: 4000
                });
                return;
            }

            this.cargando = true;

            api.post('negocios/login', this.formulario)
                .then((respuesta) => {
                    const datos = respuesta.data || {};
                    iniciarSesion(datos.access_token, datos.anunciante);
                    this.$router.push({ path: '/negocios/portal' });
                })
                .catch((error) => {
                    const estado = error && error.response ? error.response.status : 0;
                    this.$toast.add({
                        severity: 'error',
                        summary: estado === 403 ? 'Cuenta no habilitada' : 'No pudimos iniciar sesión',
                        detail: mensajeDeError(error, 'Correo o contraseña incorrectos.'),
                        life: 6000
                    });
                })
                .finally(() => {
                    this.cargando = false;
                });
        },

        entrarConGoogle(idToken) {
            this.cargando = true;
            registrarEvento(EVENTOS.CLIC_GOOGLE);

            api.post('negocios/google', { id_token: idToken })
                .then((respuesta) => {
                    const cuerpo = respuesta.data || {};

                    if (cuerpo.data && cuerpo.data.existe) {
                        iniciarSesion(cuerpo.access_token, cuerpo.anunciante);
                        this.$router.push({ path: '/negocios/portal' });
                        return;
                    }

                    // Google válido pero sin cuenta: se continúa en el registro.
                    this.$toast.add({
                        severity: 'info',
                        summary: 'Aún no tienes cuenta',
                        detail: 'Te llevamos al registro para completar los datos de tu negocio.',
                        life: 5000
                    });
                    this.$router.push({ path: '/negocios/registro' });
                })
                .catch((error) => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'No pudimos validar tu cuenta de Google',
                        detail: mensajeDeError(error),
                        life: 5000
                    });
                })
                .finally(() => {
                    this.cargando = false;
                });
        },

        enviarCodigo() {
            if (!this.recuperar.email) {
                this.$toast.add({ severity: 'warn', summary: 'Ingresa tu correo', life: 4000 });
                return;
            }

            this.cargando = true;

            api.post('negocios/password/solicitar', { email: this.recuperar.email })
                .then((respuesta) => {
                    this.codigoEnviado = true;
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Revisa tu correo',
                        detail: (respuesta.data && respuesta.data.message) ||
                            'Si el correo está registrado, te enviamos un código.',
                        life: 6000
                    });
                })
                .catch((error) => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'No pudimos enviar el código',
                        detail: mensajeDeError(error),
                        life: 5000
                    });
                })
                .finally(() => {
                    this.cargando = false;
                });
        },

        restablecer() {
            const codigo = (this.recuperar.codigo || '').replace(/\D/g, '');

            if (codigo.length !== 6) {
                this.$toast.add({ severity: 'warn', summary: 'El código debe tener 6 dígitos', life: 4000 });
                return;
            }

            if ((this.recuperar.password || '').length < 8) {
                this.$toast.add({ severity: 'warn', summary: 'La contraseña debe tener al menos 8 caracteres', life: 4000 });
                return;
            }

            if (this.recuperar.password !== this.recuperar.password_confirmation) {
                this.$toast.add({ severity: 'warn', summary: 'Las contraseñas no coinciden', life: 4000 });
                return;
            }

            this.cargando = true;

            api.post('negocios/password/restablecer', {
                email: this.recuperar.email,
                codigo: codigo,
                password: this.recuperar.password,
                password_confirmation: this.recuperar.password_confirmation
            })
                .then(() => {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Contraseña actualizada',
                        detail: 'Ya puedes iniciar sesión con tu nueva contraseña.',
                        life: 6000
                    });
                    this.formulario.email = this.recuperar.email;
                    this.recuperar = { email: '', codigo: '', password: '', password_confirmation: '' };
                    this.volverALogin();
                })
                .catch((error) => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'No pudimos cambiar tu contraseña',
                        detail: mensajeDeError(error),
                        life: 5000
                    });
                })
                .finally(() => {
                    this.cargando = false;
                });
        }
    }
};
</script>

<style lang="scss" scoped>
/* Layout: marca a la izquierda, formulario a la derecha, pie a lo ancho.
   En móvil el panel de marca se oculta y queda solo el formulario. */
.sp-auth {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;
    min-height: 100vh;
    background: var(--sp-surface-alt);
}

/* ------------------------------- marca ------------------------------- */

.sp-auth__marca {
    display: none;
    position: relative;
    isolation: isolate;
    overflow: hidden;
    padding: clamp(2rem, 4vw, 3.5rem);
    background: var(--sp-navy-900);
}

.sp-auth__fondo {
    position: absolute;
    inset: 0;
    z-index: -2;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: 55% 60%;
    }
}

.sp-auth__velo {
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(
        165deg,
        rgba(8, 31, 45, 0.92) 0%,
        rgba(8, 31, 45, 0.74) 45%,
        rgba(8, 31, 45, 0.42) 100%
    );
}

.sp-auth__marca-contenido {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-width: 34rem;
}

.sp-auth__logo {
    display: inline-flex;
    align-self: flex-start;
}

.sp-auth__logo--movil {
    display: inline-flex;
    align-self: center;
    margin-bottom: 1.5rem;
    /* En móvil el fondo es claro: el logo blanco necesita una pastilla oscura */
    padding: 0.55rem 0.9rem;
    background: var(--sp-navy);
    border-radius: 999px;
}


.sp-auth__titular {
    margin-top: clamp(2rem, 5vh, 4rem);
    color: #fff;
    font-size: clamp(1.95rem, 2.9vw, 3.5rem);
    font-weight: 800;
    line-height: 1.2;
    letter-spacing: -0.02em;
}

.sp-auth__acento {
    display: block;
    width: fit-content;
    padding-bottom: 0.35rem;
    border-bottom: 4px solid var(--sp-orange);
    color: var(--sp-orange);
}

.sp-auth__bajada {
    margin-top: 1.1rem;
    max-width: 30rem;
    color: rgba(255, 255, 255, 0.82);
    font-size: clamp(1.05rem, 1.2vw, 1.28rem);
    line-height: 1.6;
}

.sp-auth__beneficios {
    display: flex;
    flex-direction: column;
    gap: 1.35rem;
    margin-top: clamp(1.75rem, 4vh, 2.75rem);

    li {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
    }

    strong {
        display: block;
        color: #fff;
        font-size: 1.12rem;
        font-weight: 700;
    }

    /* Acotado a > li > div > span a proposito: un simple `span` tambien
       alcanzaria a .sp-auth__beneficio-icono y, por ser mas especifico que su
       propia clase, le pisaria el inline-flex y descentraria el icono. */
    > li > div > span {
        display: block;
        margin-top: 0.15rem;
        color: rgba(255, 255, 255, 0.72);
        font-size: 1rem;
        line-height: 1.55;
    }
}

.sp-auth__beneficio-icono {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 3.3rem;
    height: 3.3rem;
    background: linear-gradient(135deg, var(--sp-orange) 0%, var(--sp-orange-400) 100%);
    border-radius: 14px;
    color: #fff;
    font-size: 1.3rem;
    box-shadow: 0 6px 18px rgba(235, 93, 27, 0.35);
}

/* ------------------------------ panel -------------------------------- */

.sp-auth__panel {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: clamp(1.25rem, 3vw, 2.5rem);
}

.sp-auth__utilidades {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 1.25rem;
    width: 100%;
    margin-bottom: clamp(1.5rem, 4vh, 3rem);
    color: var(--sp-text-soft);
    font-size: 0.88rem;
}

.sp-auth__idioma {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
}

.sp-auth__ayuda {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0;
    background: none;
    border: 0;
    color: var(--sp-text-soft);
    font: inherit;
    cursor: pointer;

    i {
        color: var(--sp-orange);
    }

    &:hover,
    &:focus-visible {
        color: var(--sp-navy);
    }
}

.sp-auth__tarjeta {
    display: flex;
    flex-direction: column;
    width: 100%;
    /* Antes 32rem fijos (448px con root de 14px): en un 27" el panel derecho
       mide 1344px y la tarjeta se veia minuscula, con 896px de aire al lado. */
    max-width: 34rem;
    padding: clamp(2rem, 3vw, 3.25rem);
    background: var(--sp-surface);
    border: 1px solid var(--sp-border);
    border-radius: var(--sp-radius-lg);
    box-shadow: 0 18px 48px rgba(13, 48, 68, 0.1);
    text-align: center;

    form {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-top: 1.5rem;
        text-align: left;
    }
}

.sp-auth__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: center;
    width: 5.2rem;
    height: 5.2rem;
    background: var(--sp-orange-50);
    border-radius: 50%;
    color: var(--sp-orange);
    font-size: 2rem;
}

.sp-auth__titulo {
    margin-top: 1rem;
    color: var(--sp-navy);
    font-size: clamp(1.75rem, 2.4vw, 2.5rem);
    font-weight: 800;
    letter-spacing: -0.02em;
}

.sp-auth__sub {
    margin-top: 0.5rem;
    color: var(--sp-text-soft);
    font-size: 1.08rem;
}

/* Icono dentro del campo */
.sp-campo-icono {
    position: relative;
    display: block;

    > i {
        position: absolute;
        top: 50%;
        left: 1rem;
        transform: translateY(-50%);
        z-index: 1;
        color: #9aa8b2;
        font-size: 1rem;
        pointer-events: none;
    }

    :deep(.p-inputtext) {
        padding-left: 2.85rem;
    }

    :deep(.p-password) {
        width: 100%;
    }
}

.sp-auth__olvide {
    align-self: flex-end;
    margin-top: -0.35rem;
}

.sp-auth__enviar {
    margin-top: 0.5rem;
    min-height: 58px;
    font-size: 1.15rem;
}

.sp-auth__separador {
    position: relative;
    margin: 1.5rem 0;

    &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        height: 1px;
        background: var(--sp-border);
    }

    span {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.9rem;
        height: 1.9rem;
        background: var(--sp-surface);
        border: 1px solid var(--sp-border);
        border-radius: 50%;
        color: var(--sp-text-soft);
        font-size: 0.82rem;
    }
}

.sp-auth__google {
    display: flex;
    justify-content: center;
    min-height: 44px;
}

.sp-auth__registro {
    margin-top: 1.5rem;
    color: var(--sp-text-soft);
    font-size: 0.9rem;

    a {
        display: block;
        margin-top: 0.35rem;
        color: var(--sp-enlace);
        font-weight: 700;
        text-decoration: none;

        &:hover,
        &:focus-visible {
            text-decoration: underline;
        }
    }
}

.sp-auth__reset {
    padding-top: 1.25rem;
    border-top: 1px solid var(--sp-border);
}

.sp-auth__volver {
    align-self: center;
    margin-top: 1.25rem;
}

.sp-enlace {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    background: none;
    border: 0;
    color: var(--sp-enlace);
    font-family: inherit;
    font-size: 0.87rem;
    font-weight: 600;
    cursor: pointer;

    &:hover,
    &:focus-visible {
        text-decoration: underline;
    }
}

.sp-campo__ayuda {
    color: var(--sp-text-soft);
    font-size: 0.78rem;
}

/* -------------------------------- pie -------------------------------- */

.sp-auth__pie {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem clamp(1.25rem, 3vw, 2.5rem);
    background: var(--sp-navy-900);
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.85rem;
    text-align: center;
}


.sp-auth__pie-enlaces {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1.25rem;

    a {
        color: rgba(255, 255, 255, 0.7);

        &:hover,
        &:focus-visible {
            color: var(--sp-orange);
            text-decoration: underline;
        }
    }
}

/* --------------------------- adaptaciones ---------------------------- */

@media (min-width: 992px) {
    .sp-auth {
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    }

    .sp-auth__marca {
        display: block;
    }

    .sp-auth__logo--movil {
        display: none;
    }

    .sp-auth__panel {
        justify-content: center;
        padding: clamp(2rem, 4vw, 4rem);
    }

    .sp-auth__utilidades {
        position: absolute;
        top: clamp(1.25rem, 2.5vw, 2rem);
        right: clamp(1.5rem, 3vw, 3rem);
        width: auto;
        margin-bottom: 0;
        justify-content: flex-end;
    }

    .sp-auth__pie {
        flex-direction: row;
        justify-content: space-between;
        text-align: left;
    }
}

@media (min-width: 1200px) {
    .sp-auth {
        grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
    }
}

/* Móvil/tablet en horizontal: hay poca altura, se compacta todo. */
@media (orientation: landscape) and (max-height: 560px) {
    .sp-auth {
        min-height: auto;
    }

    .sp-auth__avatar {
        width: 3.2rem;
        height: 3.2rem;
        font-size: 1.4rem;
    }

    .sp-auth__utilidades {
        margin-bottom: 1rem;
    }
}

/* --------------------------------------------------------------------------
   Comodidad de los campos
   Los campos de la landing (13px de texto, 39px de alto) son demasiado
   pequenos aqui: en esta pantalla el formulario ES el contenido principal.
   -------------------------------------------------------------------------- */
.sp-auth__tarjeta {
    /* Filo superior en degradado: detalle de marca para que la tarjeta no sea
       un rectangulo blanco liso. */
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(90deg, var(--sp-orange) 0%, var(--sp-orange-400) 55%, var(--sp-navy) 100%);
    }

    /* En variables a proposito: si el media query de pantallas grandes usara
       el atajo `padding`, reseteaba el padding-left que reserva el hueco del
       icono y el placeholder se solapaba con el. */
    --sp-inp-x: 1.15rem;
    --sp-inp-y: 0.95rem;

    :deep(.p-inputtext),
    :deep(.p-inputtextarea) {
        padding: var(--sp-inp-y) var(--sp-inp-x);
        font-size: 1.08rem;
        border-radius: 14px;
    }

    .sp-campo label {
        font-size: 1rem;
    }
}

.sp-campo-icono {
    > i {
        font-size: 1.15rem;
    }

    /* El hueco del icono se calcula sobre el padding base, asi que se mantiene
       aunque cambien los valores en pantallas grandes. */
    :deep(.p-inputtext) {
        padding-left: calc(var(--sp-inp-x) + 2.1rem);
    }
}

.sp-auth__eyebrow {
    align-self: flex-start;
    margin-top: clamp(1.75rem, 4vh, 3rem);
    margin-bottom: 0;
}

/* El titular ya no necesita el margen superior: lo aporta el eyebrow. */
.sp-auth__titular {
    margin-top: 1.1rem;
}

/* --------------------------------------------------------------------------
   Monitores grandes: sin esto la tarjeta se quedaba en 448px dentro de un
   panel de 1344px, que es la causa de que se viera pequena.
   -------------------------------------------------------------------------- */
@media (min-width: 2000px) {
    .sp-auth__tarjeta {
        max-width: 40rem;
        padding: 3.6rem;
    }

    .sp-auth__titulo {
        font-size: 3rem;
    }

    .sp-auth__sub {
        font-size: 1.22rem;
    }

    .sp-auth__avatar {
        width: 6rem;
        height: 6rem;
        font-size: 2.6rem;
    }

    .sp-auth__enviar {
        min-height: 64px;
        font-size: 1.25rem;
    }

    .sp-auth__tarjeta {
        --sp-inp-x: 1.3rem;
        --sp-inp-y: 1.1rem;

        :deep(.p-inputtext) {
            font-size: 1.18rem;
        }

        .sp-campo label {
            font-size: 1.08rem;
        }
    }

    .sp-auth__beneficios {
        gap: 1.7rem;

        strong { font-size: 1.24rem; }
        > li > div > span { font-size: 1.1rem; }
    }

    .sp-auth__beneficio-icono {
        width: 3.8rem;
        height: 3.8rem;
        font-size: 1.5rem;
    }
}
</style>
